/**
 * Request Caching Utility
 * 
 * This utility provides a simple way to cache and deduplicate API requests
 * to prevent duplicate network calls when multiple components request the same data.
 */

type CacheEntry<T> = {
  data: T;
  timestamp: number;
  promise?: Promise<T>;
};

// Global cache
const globalCache = new Map<string, CacheEntry<any>>();

interface CacheOptions {
  /** Time in ms that the cache remains valid */
  ttl?: number;
  /** Force refresh the cache regardless of TTL */
  forceRefresh?: boolean;
}

const defaultOptions: CacheOptions = {
  ttl: 30000, // 30 seconds default
  forceRefresh: false
};

/**
 * Enhanced cache implementation with better promise handling and error recovery
 * 
 * @param cacheKey A unique key for the request
 * @param fetchFn The function that fetches the data
 * @param options Caching options
 * @returns The cached or freshly fetched data
 */
export async function cachedRequest<T>(
  cacheKey: string,
  fetchFn: () => Promise<T>,
  options: CacheOptions = {}
): Promise<T> {
  const mergedOptions = { ...defaultOptions, ...options };
  const { ttl, forceRefresh } = mergedOptions;
  
  // Make the cache key more robust by trimming and normalizing
  const normalizedCacheKey = cacheKey.trim().toLowerCase();
  
  // Check if we already have a valid cached entry
  if (!forceRefresh && globalCache.has(normalizedCacheKey)) {
    const cachedEntry = globalCache.get(normalizedCacheKey)!;
    const now = Date.now();
    
    // If cache is still valid, return it
    if (now - cachedEntry.timestamp < ttl!) {
      console.log(`Using cached response for: ${normalizedCacheKey} (${Math.round((now - cachedEntry.timestamp) / 1000)}s old)`);
      return cachedEntry.data;
    }
  }
  
  // Check if we already have a pending request for this key
  const existingEntry = globalCache.get(normalizedCacheKey);
  if (existingEntry?.promise) {
    console.log(`Pending request found for: ${normalizedCacheKey}, reusing promise`);
    
    try {
      // Wait for the existing promise to complete
      return await existingEntry.promise;
    } catch (error) {
      // If promise fails, we'll continue to make a new request
      console.error(`Cached promise for ${normalizedCacheKey} failed, retrying:`, error);
    }
  }
  
  // Create a new request
  console.log(`Fetching fresh data for: ${normalizedCacheKey}`);
  
  // Create promise
  const fetchPromise = fetchFn();
  
  // Store the promise in cache temporarily
  const promiseEntry: CacheEntry<T> = {
    data: null as any,
    timestamp: Date.now(),
    promise: fetchPromise
  };
  
  globalCache.set(normalizedCacheKey, promiseEntry);
  
  try {
    // Await the real result
    const data = await fetchPromise;
    
    // Update cache with actual data
    const dataEntry: CacheEntry<T> = {
      data,
      timestamp: Date.now()
    };
    
    // Only update cache if the request was successful and returned valid data
    if (data !== null && data !== undefined) {
      globalCache.set(normalizedCacheKey, dataEntry);
    } else {
      console.warn(`Request for ${normalizedCacheKey} returned null/undefined data, not caching`);
      globalCache.delete(normalizedCacheKey);
    }
    
    return data;
  } catch (error) {
    // Remove failed promise from cache
    globalCache.delete(normalizedCacheKey);
    console.error(`Request for ${normalizedCacheKey} failed:`, error);
    throw error;
  }
}

/**
 * Invalidate a specific cache entry
 * 
 * @param cacheKey The key to invalidate
 */
export function invalidateCache(cacheKey: string): void {
  globalCache.delete(cacheKey);
}

/**
 * Invalidate all cache entries that match a prefix
 * 
 * @param keyPrefix The prefix to match
 */
export function invalidateCacheByPrefix(keyPrefix: string): void {
  for (const key of globalCache.keys()) {
    if (key.startsWith(keyPrefix)) {
      globalCache.delete(key);
    }
  }
}

/**
 * Clear the entire cache
 */
export function clearCache(): void {
  globalCache.clear();
}

/**
 * Create a stable JSON key for an object with improved handling of complex types
 * 
 * @param obj The object to create a key for
 * @returns A stable JSON string representation
 */
export function createStableKey(obj: Record<string, any>): string {
  if (!obj) return 'null';
  
  try {
    // Sort keys alphabetically to ensure stable output
    const sortedObj: Record<string, any> = {};
    
    // Get all keys and sort them
    const keys = Object.keys(obj).sort();
    
    // Rebuild object with sorted keys
    for (const key of keys) {
      if (obj[key] === undefined) continue; // Skip undefined values
      
      let value = obj[key];
      
      // Handle array values by sorting them if they're primitives
      if (Array.isArray(value)) {
        if (value.length === 0) {
          value = "[]"; // Handle empty arrays consistently
        } else if (value.every(item => typeof item !== 'object' || item === null)) {
          value = [...value].sort();
        } else {
          // For arrays of objects, sort each object and then the array
          value = value.map(item => 
            typeof item === 'object' && item !== null 
              ? createStableKey(item as Record<string, any>)
              : String(item)
          ).sort();
        }
      } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        // Recursively sort nested objects
        value = createStableKey(value);
      } else if (value === null) {
        value = "null"; // Consistent null handling
      }
      
      sortedObj[key] = value;
    }
    
    return JSON.stringify(sortedObj);
  } catch (error) {
    console.error('Error creating stable key:', error);
    // Fallback to simple stringification with a timestamp to ensure uniqueness
    return JSON.stringify({
      originalObject: String(obj),
      timestamp: Date.now()
    });
  }
}
